import { useWallet } from '@txnlab/use-wallet-react';
import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { resolveNFD } from '../lib/nfd';
import { LogOut, ChevronDown, User } from 'lucide-react';

export default function WalletConnect() {
    // ──── ALL HOOKS FIRST (BEFORE ANY CONDITIONALS) ────
    const { 
        activeAccount, 
        socialUser,
        guestUser, 
        isVerified, 
        isVerifying, 
        verifyAccount,
        authMethod,
        signOut,
        setShowAuthModal,
        userProfile
    } = useAuth();
    const { activeWallet } = useWallet();
    const [nfdName, setNfdName] = useState<string | null>(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // All effects BEFORE any conditionals
    useEffect(() => {
        if (!activeAccount) { setNfdName(null); return; }

        const cacheKey = `nfd_${activeAccount.address}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
            setNfdName(cached);
            return;
        }

        let active = true;
        resolveNFD(activeAccount.address).then(name => {
            if (active) {
                setNfdName(name);
                if (name) {
                    sessionStorage.setItem(cacheKey, name);
                }
            }
        });
        return () => { active = false; };
    }, [activeAccount?.address]);

    useEffect(() => {
        if (!showDropdown) return;
        const handler = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showDropdown]);

    // ──── NOW COMPUTE VALUES ────
    const isConnected = (activeAccount && isVerified) || socialUser;
    const isGuest = !!guestUser && !activeAccount && !socialUser;
    const isDisconnected = !authMethod && !activeAccount && !guestUser;
    const isUnverifiedWallet = activeAccount && !isVerified;
    
    // Priority: NFD -> User Profile Name -> Wallet Address -> Social Email -> Fallback
    const displayText = nfdName ?? userProfile?.displayName ?? (activeAccount ? `${activeAccount.address.slice(0, 6)}…${activeAccount.address.slice(-4)}` : (socialUser?.email || 'User'));
    const walletIcon = activeWallet?.metadata?.icon;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    const handleDisconnect = async () => {
        setShowDropdown(false);
        await signOut();
        await activeWallet?.disconnect();
    };

    // ──── NOW RENDER (NO EARLY RETURNS) ────
    
    // Disconnected state
    if (isDisconnected) {
        return (
            <div className="flex flex-col items-end gap-2">
                <div className="flex items-center bg-pit-green/5 rounded-lg border border-pit-green/20 p-1 gap-1">
                    <button
                        id="connect-wallet-btn"
                        onClick={() => setShowAuthModal(true)}
                        className="px-5 py-2 rounded-md bg-[#39FF14] hover:bg-white text-black font-black uppercase text-xs transition-all shadow-[0_0_15px_rgba(57,255,20,0.2)] hover:shadow-[0_0_30px_rgba(57,255,20,0.5)] cursor-pointer tracking-widest"
                    >
                        Login
                    </button>
                </div>
                {isIOS && (
                    <div className="text-[9px] text-orange-400 font-bold uppercase tracking-tighter bg-orange-400/5 px-2 py-1 rounded-md border border-orange-400/20">
                        iPhone? Disable "NSURLSession WebSocket" in Safari settings if connection fails.
                    </div>
                )}
            </div>
        );
    }

    // Guest state
    if (isGuest) {
        return (
            <button
                onClick={() => setShowAuthModal(true)}
                className="px-5 py-2 rounded-md bg-[#39FF14] hover:bg-white text-black font-black uppercase text-xs transition-all shadow-[0_0_15px_rgba(57,255,20,0.2)] hover:shadow-[0_0_30px_rgba(57,255,20,0.5)] cursor-pointer tracking-widest"
            >
                Login
            </button>
        );
    }

    // Connected and verified state
    if (isConnected) {
        return (
            <div className="relative" ref={dropdownRef}>
                <button
                    onClick={() => setShowDropdown(v => !v)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all cursor-pointer group border ${!isVerified
                            ? 'bg-orange-500/10 hover:bg-orange-500/20 border-orange-500/30 hover:border-orange-500/60'
                            : 'bg-[#39FF14]/8 hover:bg-[#39FF14]/15 border-[#39FF14]/25 hover:border-[#39FF14]/50'
                        }`}
                >
                    {!isVerified ? (
                        <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse flex-none" />
                    ) : walletIcon ? (
                        <img src={walletIcon} alt="" className="w-4 h-4 rounded-sm flex-none" />
                    ) : socialUser ? (
                        <User className="w-4 h-4 text-[#39FF14] flex-none" />
                    ) : (
                        <span className="w-2 h-2 rounded-full bg-[#39FF14] animate-pulse flex-none" />
                    )}

                    <span className={`font-mono font-black text-xs uppercase tracking-widest leading-none ${!isVerified ? 'text-orange-400' : 'text-[#39FF14]'
                        }`}>
                        {isVerifying ? 'Verifying...' : displayText}
                    </span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${showDropdown ? 'rotate-180' : ''} ${!isVerified ? 'text-orange-500/60' : 'text-[#39FF14]/60'
                        }`} />
                </button>

                <AnimatePresence>
                    {showDropdown && (
                        <motion.div
                            initial={{ opacity: 0, y: -6, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -4, scale: 0.97 }}
                            transition={{ duration: 0.12 }}
                            className="absolute right-0 mt-2 w-52 bg-[#0d0d0d] border border-white/10 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.8)] overflow-hidden z-50"
                        >
                            <div className="px-4 py-3 border-b border-white/5">
                                <div className="text-[9px] text-slate-600 font-bold uppercase tracking-widest mb-0.5">Connected</div>
                                <div className={`font-mono text-xs truncate font-black ${!isVerified ? 'text-orange-400' : 'text-[#39FF14]'}`}>
                                    {displayText}
                                </div>
                            </div>

                            {!isVerified && (
                                <div className="p-3 border-b border-white/5 bg-orange-500/5">
                                    <button
                                        onClick={() => {
                                            setShowDropdown(false);
                                            verifyAccount();
                                        }}
                                        disabled={isVerifying}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-400 text-black font-black text-[10px] uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(249,115,22,0.2)]"
                                    >
                                        {isVerifying ? 'Verifying...' : 'Verify Ownership'}
                                    </button>
                                    <p className="text-[8px] text-orange-500/60 font-medium uppercase tracking-tight mt-1.5 text-center px-1">
                                        Prove ownership to sync stats (Free)
                                    </p>
                                </div>
                            )}

                            <button
                                onClick={handleDisconnect}
                                className="w-full flex items-center gap-2.5 px-4 py-3 text-left text-slate-400 hover:text-red-400 hover:bg-red-500/5 transition-colors text-xs font-black uppercase tracking-widest"
                            >
                                <LogOut className="w-3.5 h-3.5" />
                                Disconnect
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        );
    }

    // Unverified wallet state
    if (isUnverifiedWallet) {
        return (
            <div className="flex items-center gap-2">
                <button
                    onClick={verifyAccount}
                    disabled={isVerifying}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all cursor-pointer bg-orange-500 hover:bg-orange-400 text-black border border-orange-500/30"
                >
                    <span className="font-mono font-black text-xs uppercase tracking-widest leading-none">
                        {isVerifying ? 'Verifying...' : 'Verify Ownership'}
                    </span>
                </button>
                <button
                    onClick={handleDisconnect}
                    className="p-1.5 rounded-lg hover:bg-white/5 transition-colors border border-white/10"
                >
                    <LogOut className="w-3.5 h-3.5 text-slate-400" />
                </button>
            </div>
        );
    }

    return null;
}
